import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { GridValueFormatter } from "@mui/x-data-grid-pro";
import { DatePicker } from "@mui/x-date-pickers";
import { isFulfilled } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch } from "../../../app/hooks";
import DialogComponent from "../../../common/components/functional/dialogs/DialogComponent";
import { Heading1 } from "../../../common/components/styled/styled";
import { leadingZerosDateFormat } from "../../../common/helpers/formatDateTime";
import useMessage from "../../../hooks/useMessage";
import { FormContainer } from "../../customer/customerManagement/styles";
import { DataGrid } from "../helpers/DataGrid";
import { SCHEDULING_PERIOD_TYPES } from "../helpers/utils";
import {
  addSchedulingPeriod,
  fetchSchedulingPeriods,
  updateSchedulingPeriod,
} from "../settingsActions";
import { SchedulingPeriod } from "../settingsApi";
import { AddSchedulingPeriod } from "./AddSchedulingPeriod";
import { SchedulingPeriodView } from "./SchedulingPeriodView";

export const SchedulingPeriodsControlPanel = () => {
  const dispatch = useAppDispatch();
  const { sendMessage } = useMessage();
  const { t } = useTranslation("schedulingPeriods");

  const [selectedType, setSelectedType] = useState<string>("");
  const [selectedYear, setSelectedYear] = useState<Date>(new Date());
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<SchedulingPeriod>({
    start: "",
    end: "",
    entriesApprovedByCustomer: 0,
    entriesApprovedBySystem: 0,
    invoicesSentWave1: 0,
    invoicesSentWave2: 0,
    salaryDate: "",
    valueDate: "",
  });

  const [schedulingPeriodsVersion, setSchedulingPeriodsVersion] =
    useState<number>(0);
  const [schedulingPeriods, setSchedulingPeriods] = useState<
    SchedulingPeriod[]
  >([]);
  const [latestScheduleEnd, setLatestScheduleEnd] = useState<
    string | undefined
  >();

  const selectType = (event: SelectChangeEvent) => {
    setSelectedType(event.target.value as string);
  };

  const gridDateFormatter: GridValueFormatter = (value: string) =>
    format(new Date(value), leadingZerosDateFormat);
  const columns = [
    {
      field: "start",
      headerName: t("start", { ns: "common" }),
      width: 250,
      editable: false,
      valueFormatter: gridDateFormatter,
    },
    {
      field: "end",
      headerName: t("end", { ns: "common" }),
      width: 250,
      editable: false,
      valueFormatter: gridDateFormatter,
    },
    {
      field: "salaryDate",
      headerName: t("salaryDate"),
      width: 250,
      editable: false,
      valueFormatter: gridDateFormatter,
    },
    {
      field: "valueDate",
      headerName: t("valueDate"),
      width: 250,
      editable: false,
      valueFormatter: gridDateFormatter,
    },
  ];

  const openRow = (row: SchedulingPeriod) => {
    setIsModalOpen(true);
    setSelectedRow(row);
  };

  // TODO display a more prominent message box with error messages

  const saveRow = (row: SchedulingPeriod) => {
    setIsModalOpen(false);
    dispatch(
      updateSchedulingPeriod({
        classId: selectedType,
        version: schedulingPeriodsVersion,
        period: row,
      }),
    ).then((action) => {
      if (isFulfilled(action)) {
        action.payload.forEach(({ version, payload }) => {
          const updatedPeriods = schedulingPeriods.map((schedulingPeriod) => {
            if (schedulingPeriod.start === payload.schedulingPeriod.start) {
              return {
                ...payload.schedulingPeriod,
                id: payload.schedulingPeriod.start,
              };
            } else {
              return schedulingPeriod;
            }
          });

          setSchedulingPeriodsVersion(version);
          setSchedulingPeriods(updatedPeriods);
          sendMessage(t("SAVING_SUCCESS", { ns: "common" }), "success");
        });
      }
    });
  };

  const addNewSchedulingPeriod = (schedulingPeriod: SchedulingPeriod) => {
    dispatch(
      addSchedulingPeriod({
        classId: selectedType,
        version: schedulingPeriodsVersion,
        period: schedulingPeriod,
      }),
    ).then((action) => {
      if (isFulfilled(action)) {
        action.payload.forEach(({ version, payload }) => {
          const newSchedulingPeriod = {
            ...payload.schedulingPeriod,
            id: payload.schedulingPeriod.start,
          };

          setSchedulingPeriodsVersion(version);
          setSchedulingPeriods([...schedulingPeriods, newSchedulingPeriod]);
          sendMessage(t("SAVING_SUCCESS", { ns: "common" }), "success");
        });
      }
    });
  };

  useEffect(() => {
    if (!!selectedType && !!selectedYear) {
      const year = selectedYear.getUTCFullYear().toString();
      dispatch(
        fetchSchedulingPeriods({ schedulingClassId: selectedType, year }),
      ).then((action) => {
        if (isFulfilled(action)) {
          // scheduling periods might not have been created yet for future time
          const periods = action.payload.periods?.map((schedulingPeriod) => ({
            ...schedulingPeriod,
            id: schedulingPeriod.start,
          }));

          setSchedulingPeriodsVersion(action.payload.version);
          setSchedulingPeriods(periods || []);
        }
      });
    }
  }, [selectedType, selectedYear, dispatch]);

  useEffect(() => {
    const latestEndDate = schedulingPeriods.length
      ? schedulingPeriods[schedulingPeriods.length - 1].end
      : undefined;
    setLatestScheduleEnd(latestEndDate);
  }, [schedulingPeriods]);

  return (
    <FormContainer>
      <Heading1>{t("schedulingAndBillingPeriods")}</Heading1>
      <Grid container spacing={3}>
        <Grid item sm={12}>
          <FormControl fullWidth>
            <InputLabel>{t("periodsType")}</InputLabel>
            <Select
              value={selectedType}
              label={t("periodsType")}
              onChange={selectType}
            >
              {SCHEDULING_PERIOD_TYPES.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {t(type.name)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item sm={6}>
          <FormControl fullWidth>
            <DatePicker
              views={["year"]}
              label={t("selectYear")}
              value={selectedYear}
              onChange={(newValue) => {
                if (!!newValue) {
                  setSelectedYear(newValue);
                }
              }}
              slotProps={{ textField: { helperText: null } }}
            />
          </FormControl>
        </Grid>
      </Grid>
      <AddSchedulingPeriod
        type={selectedType}
        selectedYear={selectedYear?.getFullYear().toString()}
        previousScheduleEndDate={latestScheduleEnd}
        onSave={addNewSchedulingPeriod}
      />
      <DataGrid
        columns={columns}
        rows={schedulingPeriods}
        hideSave={true}
        onOpenRow={(row) => openRow(row)}
        actions={["open"]}
        disableScrollToBottomOnAddRow
      />
      <DialogComponent
        autoWidth
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
      >
        <SchedulingPeriodView
          type={selectedType}
          isNewSchedulingPeriod={false}
          row={selectedRow}
          onClose={() => setIsModalOpen(false)}
          onSave={saveRow}
        />
      </DialogComponent>
    </FormContainer>
  );
};
